import type { IDescriptionSection } from '@atlas/types'
import { SectionCard } from '@atlas/ui-components'
import { Link } from 'react-router-dom'
import { SafeHtml } from '@/components/ui/SafeHtml'
import { ExternalDescription } from '@/components/work-orders/ExternalDescription'

export interface RequestDetailsSectionProps {
	/** The order's briefing, as sanitised HTML. */
	briefing: string | null
	/** The Workfront brief's sections, for an order synced from it. */
	externalDescription?: IDescriptionSection[] | null
	brand: string | null
	market: string | null
	parent: { workOrderId: string; orderCode: string } | null
	assetTypeCode: string | null
	complexityLevel: string | null
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div>
			<dt className="text-xs text-ds-text-muted">{label}</dt>
			<dd className="mt-1 text-sm text-ds-text">{children}</dd>
		</div>
	)
}

/** What was asked for, read-only: the brief, then the descriptors a designer checks it against. */
export function RequestDetailsSection(props: RequestDetailsSectionProps) {
	const { brand, market, parent, assetTypeCode, complexityLevel } = props
	return (
		<SectionCard title="Review incoming request" collapsible>
			<h3 className="text-xs text-ds-text-muted">Description</h3>
			{props.briefing ? (
				<SafeHtml
					html={props.briefing}
					className="prose prose-sm mt-1 max-w-none text-sm leading-relaxed text-ds-text [&_a]:text-ds-primary [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
				/>
			) : (
				<p className="mt-1 text-sm text-ds-text-subtle">No description.</p>
			)}
			<ExternalDescription sections={props.externalDescription ?? null} />
			<dl className="mt-5 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3">
				{brand ? <Detail label="Brand">{brand}</Detail> : null}
				{market ? <Detail label="Market">{market}</Detail> : null}
				{parent ? (
					<Detail label="Parent factory">
						<Link to={`/work-orders/${parent.workOrderId}`} className="text-ds-primary hover:underline">
							{parent.orderCode}
						</Link>
					</Detail>
				) : null}
				{assetTypeCode ? <Detail label="Classification">{assetTypeCode}</Detail> : null}
				{complexityLevel ? <Detail label="Complexity">{complexityLevel}</Detail> : null}
			</dl>
		</SectionCard>
	)
}
